import { Link } from 'react-router-dom';
import type { Listing } from '@shared/types';
import { getListingPath } from '../lib/listingPaths';
import { formatListingLocationLine } from '../lib/locationRegions';
import { stripHtml } from '../lib/content';
import { getListingPriceDisplay } from '../lib/pricing';

interface ListingCardProps {
  listing: Listing;
}

export default function ListingCard({ listing }: ListingCardProps) {
  const priceDisplay = getListingPriceDisplay(listing);
  const locationLine = formatListingLocationLine(listing);
  const coverImage = listing.coverImageUrl || (listing.images && listing.images[0]);
  const description = stripHtml(listing.description || '');

  return (
    <Link to={getListingPath(listing)} className="block h-full">
      <div className="card h-full flex flex-col hover:shadow-lg transition-shadow">
        {coverImage ? (
          <img
            src={coverImage}
            alt={listing.title}
            className="w-full h-48 object-cover rounded-lg mb-4"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
            }}
          />
        ) : (
          <div className="w-full h-48 bg-gray-200 rounded-lg mb-4 flex items-center justify-center text-gray-400">
            No Image
          </div>
        )}
        <h3 className="text-xl font-semibold mb-2 line-clamp-1">{listing.title}</h3>
        <p className="text-gray-600 mb-3 line-clamp-1">{locationLine}</p>
        {!!description && (
          <p className="text-gray-700 mb-4 text-sm line-clamp-2">{description}</p>
        )}
        <div className="mt-auto flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {listing.bedrooms} bedroom{listing.bedrooms !== 1 ? 's' : ''} •{' '}
            {listing.bathrooms} bathroom{listing.bathrooms !== 1 ? 's' : ''} • Sleeps {listing.capacity}
          </div>
          <div className="text-lg font-bold text-[#32373c]">
            {priceDisplay.hasVariableRates && (
              <span className="text-sm font-normal mr-1">From</span>
            )}
            R {priceDisplay.hasVariableRates ? priceDisplay.primary.split(' - ')[0] : priceDisplay.primary}{' '}
            <span className="text-sm font-normal">/ night</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

